import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import Currency from 'ui/atoms/currency';
import Date from 'ui/atoms/date';
import { DataRecordOrdering } from 'ui/types/data-ordering';
import { InvestorPortfolio } from 'ui/types/portfolio';
import { PaginationProps } from 'ui/molecules/pagination';
import { PortfolioApi, PortfolioListOrderingEnum, PortfolioValuesRetrieveFieldEnum } from 'src/api';
import useApiCall from 'src/hooks/use-api-call';

export interface DashboardPortfolioTableProps {
  /** Primary content. */
  children?: ReactNode;
  /** Additional classes. */
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  investorPortfolio?: InvestorPortfolio[];
  goToDetails: (productId: string) => void;
  loading: boolean;
  paginationProps?: PaginationProps;
}

const DashboardPortfolioTable: FunctionComponent<DashboardPortfolioTableProps> = (props) => {
  const {
    className,
    children,
    onOrderBy,
    ordering,
    goToDetails,
    investorPortfolio,
    loading,
    paginationProps,
    ...restProps
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const portfolioApi: PortfolioApi = useMemo(() => makeAuthenticatedApi(PortfolioApi), [makeAuthenticatedApi]);

  return (
    <div className={cx('dashboard-portfolio-table', className)} {...restProps}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={investorPortfolio}
        loading={loading}
        paginationProps={paginationProps}
        onGoToDetails={goToDetails}
        cells={[
          {
            title: <Translate name="dashboardPortfolioTable.productName" />,
            render: (product) => product.productName,
            orderingFieldName: PortfolioListOrderingEnum.PRODUCT_NAME_ASC,
            filterName: 'productName',
            searchFieldName: PortfolioValuesRetrieveFieldEnum.PRODUCT_NAME,
            getFilterData: (params: any) => portfolioApi.portfolioValuesRetrieve(params),
          },
          {
            title: <Translate name="dashboardPortfolioTable.totalInvestmentAmount" args={[<>&shy;</>]} />,
            orderingFieldName: PortfolioListOrderingEnum.TOTAL_INVESTMENT_AMOUNT_ASC,
            render: ({ totalInvestmentAmount }) =>
              totalInvestmentAmount && <Currency>{totalInvestmentAmount}</Currency>,
            alignContent: 'right',
            type: CellTypeEnum.currency,
            filterName: 'totalInvestmentAmount',
          },
          {
            title: <Translate name="dashboardPortfolioTable.totalEarnings" />,
            orderingFieldName: PortfolioListOrderingEnum.TOTAL_EARNINGS_ASC,
            render: ({ totalEarnings }) => totalEarnings && <Currency>{totalEarnings}</Currency>,
            alignContent: 'right',
            type: CellTypeEnum.currency,
            filterName: 'totalEarnings',
          },
          {
            title: <Translate name="dashboardPortfolioTable.lastProfit" />,
            orderingFieldName: PortfolioListOrderingEnum.LAST_PROFIT_ASC,
            render: ({ lastProfit }) => lastProfit && <Currency>{lastProfit}</Currency>,
            alignContent: 'right',
            type: CellTypeEnum.currency,
            filterName: 'lastProfit',
          },
          {
            title: <Translate name="dashboardPortfolioTable.nextProfit" />,
            orderingFieldName: PortfolioListOrderingEnum.NEXT_PROFIT_ASC,
            render: ({ nextProfit }) => nextProfit && <Date>{nextProfit}</Date>,
            alignContent: 'right',
            type: CellTypeEnum.date,
            filterName: 'nextProfit',
          },
        ]}
      />
    </div>
  );
};

export default DashboardPortfolioTable;
